import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { Analysis, UserPreferredUnit } from '@core';
import { graphLineCorridorsAtom, graphLinesByParentAtom, uiNodesAtom } from '@store';

import { graphDataFromLines } from './utils';

export type UseGraphDataParams = {
    nodeId?: string;
    selectedAnalysis: Analysis | null | undefined;
    comparisonAnalysis: Analysis | null | undefined;
    userPreferredUnit: UserPreferredUnit;
    omitNodes?: string[] | undefined;
};

export const useGraphData = ({
    nodeId,
    selectedAnalysis,
    comparisonAnalysis,
    userPreferredUnit,
    omitNodes,
}: UseGraphDataParams) => {
    const corridorsByGraphLine = useAtomValue(graphLineCorridorsAtom);
    const graphLinesByParent = useAtomValue(graphLinesByParentAtom);
    const uiNodes = useAtomValue(uiNodesAtom);
    const parentLines = graphLinesByParent[nodeId ?? ''] ?? [];
    const [graphLines, omitLines] = useMemo(() => {
        if (!uiNodes) return [undefined, undefined];
        const omittedIds = new Set(omitNodes);
        const lines = parentLines.filter((line) => !omittedIds.has(line.id));
        const omittedLines = parentLines.filter((line) => omittedIds.has(line.id));
        return [lines, omittedLines];
    }, [parentLines, omitNodes, uiNodes]);

    const includeTransitionPoints = nodeId === 'graph.transition_sequence';
    const includeApexPoints = nodeId === 'graph.timing_sequence';
    const segmentation = useMemo(() => {
        return {
            current: selectedAnalysis?.data?.analysis?.segmentation ?? null,
            comparison: comparisonAnalysis?.data?.analysis?.segmentation ?? null,
        };
    }, [selectedAnalysis, comparisonAnalysis]);

    const graphData = useMemo(
        () =>
            graphDataFromLines({
                graphLines,
                analysis: selectedAnalysis,
                comparisonAnalysis,
                segmentation,
                extras: {
                    includeTransitionPoints,
                    includeApexPoints,
                },
                userPreferredUnit,
                corridorsByGraphLine,
            }),
        [
            graphLines,
            corridorsByGraphLine,
            selectedAnalysis,
            comparisonAnalysis,
            segmentation,
            includeTransitionPoints,
            includeApexPoints,
            userPreferredUnit,
        ],
    );
    return { graphData, segmentation, graphLines, omitLines };
};
